/* eslint-disable react/no-multi-comp -- техдолг: вспомогательные компоненты вынести в отдельные файлы (docs/todos/tech-debt/admin-page-helper-components.md) */
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CafeModerationStatus } from '@cookspace-shared-contracts/core';
import { AlertTriangle, ArrowLeft, Check, X } from 'lucide-react';

import {
    useApproveCafeApplicationMutation,
    useGetCafeApplicationQuery,
    useRejectCafeApplicationMutation
} from '@/api/cafeApplicationsApi';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import type {
    CafeApplicationCompany,
    CafeApplicationDetail
} from '@/lib/cafeApplicationsSchemas';

const REJECT_REASON_MIN_LENGTH = 1;

const STATUS_LABEL: Record<CafeModerationStatus, string> = {
    pending: 'На рассмотрении',
    approved: 'Одобрена',
    rejected: 'Отклонена'
};

const STATUS_COLOR: Record<CafeModerationStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
};

const COMPANY_TYPE_LABEL: Record<CafeApplicationCompany['type'], string> = {
    LEGAL: 'Юридическое лицо',
    INDIVIDUAL: 'Индивидуальный предприниматель'
};

function formatDateTime(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleString('ru-RU');
}

interface FieldProps {
    label: string;
    value: string | null;
    testId?: string;
    wide?: boolean;
}

function Field({ label, value, testId, wide }: FieldProps): ReactElement {
    return (
        <div className={wide ? 'sm:col-span-2' : undefined}>
            <dt className="text-xs font-medium uppercase text-gray-500">{label}</dt>
            <dd className="text-gray-900" data-testid={testId}>
                {value !== null && value !== '' ? value : '—'}
            </dd>
        </div>
    );
}

interface CompanySectionProps {
    company: CafeApplicationCompany | null;
    companyContactPhone: string | null;
    companyVerifiedAt: string | null;
}

function CompanySection({
    company,
    companyContactPhone,
    companyVerifiedAt
}: CompanySectionProps): ReactElement {
    if (company === null) {
        return (
            <section data-testid="cafe-application-company">
                <h2 className="mb-3 text-base font-semibold text-gray-900">Реквизиты компании</h2>
                <p className="text-sm text-gray-500" data-testid="cafe-application-company-empty">
                    Реквизиты не верифицированы (legacy / без ИНН).
                </p>
            </section>
        );
    }

    return (
        <section data-testid="cafe-application-company">
            <h2 className="mb-3 text-base font-semibold text-gray-900">Реквизиты компании</h2>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <Field
                    label="Тип организации"
                    value={COMPANY_TYPE_LABEL[company.type]}
                    testId="cafe-application-company-type"
                />
                <Field label="ИНН" value={company.inn} testId="cafe-application-company-inn" />
                <Field label="КПП" value={company.kpp} />
                <Field label="ОГРН" value={company.ogrn} />
                <Field
                    label="Полное наименование"
                    value={company.nameFull}
                    wide
                    testId="cafe-application-company-name-full"
                />
                <Field
                    label="Краткое наименование"
                    value={company.nameShort}
                    wide
                    testId="cafe-application-company-name-short"
                />
                <Field
                    label="Статус в реестре ФНС"
                    value={company.status}
                    testId="cafe-application-company-status"
                />
                <Field label="Дата актуальности" value={company.actualityDate} />
                <Field label="Адрес" value={company.address} wide />
                <Field
                    label="Руководитель"
                    value={company.managerName}
                    testId="cafe-application-company-manager-name"
                />
                <Field label="Должность руководителя" value={company.managerPost} />
                <Field label="ОКВЭД" value={company.okved} />
                <Field
                    label="Контактный телефон"
                    value={companyContactPhone}
                    testId="cafe-application-company-phone"
                />
                <Field label="Дата верификации" value={formatDateTime(companyVerifiedAt)} />
            </dl>
        </section>
    );
}

export function CafeApplicationDetailPage(): ReactElement {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [isApproveOpen, setApproveOpen] = useState(false);
    const [approveReason, setApproveReason] = useState('');

    const [isRejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonError, setRejectReasonError] = useState<string | null>(null);

    const [actionError, setActionError] = useState<string | null>(null);

    const { data, isLoading, isError } = useGetCafeApplicationQuery(
        { id: id ?? '' },
        { skip: id === undefined || id === '' }
    );

    const [approveApplication, { isLoading: isApproving }] = useApproveCafeApplicationMutation();
    const [rejectApplication, { isLoading: isRejecting }] = useRejectCafeApplicationMutation();

    if (id === undefined || id === '') {
        return (
            <div className="text-sm text-red-600" data-testid="cafe-application-bad-id">
                Не указан ID заявки
            </div>
        );
    }

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка заявки...</div>;
    }

    if (isError || !data) {
        return (
            <div className="text-sm text-red-600" data-testid="cafe-application-error">
                Не удалось загрузить заявку
            </div>
        );
    }

    const application: CafeApplicationDetail = data.application;

    const openApprove = (): void => {
        setApproveReason('');
        setActionError(null);
        setApproveOpen(true);
    };

    const closeApprove = (): void => {
        setApproveOpen(false);
        setApproveReason('');
    };

    const openReject = (): void => {
        setRejectReason('');
        setRejectReasonError(null);
        setActionError(null);
        setRejectOpen(true);
    };

    const closeReject = (): void => {
        setRejectOpen(false);
        setRejectReason('');
        setRejectReasonError(null);
    };

    const submitApprove = async (): Promise<void> => {
        const reasonTrimmed = approveReason.trim();
        try {
            await approveApplication({
                id: application.cafeId,
                body: {
                    ...(reasonTrimmed !== '' ? { reason: reasonTrimmed } : {})
                }
            }).unwrap();
            closeApprove();
        } catch {
            setActionError('Не удалось одобрить заявку');
            closeApprove();
        }
    };

    const submitReject = async (): Promise<void> => {
        const reasonTrimmed = rejectReason.trim();
        if (reasonTrimmed.length < REJECT_REASON_MIN_LENGTH) {
            setRejectReasonError('Укажите причину отклонения');
            return;
        }
        setRejectReasonError(null);
        try {
            await rejectApplication({
                id: application.cafeId,
                body: { rejectionReason: reasonTrimmed }
            }).unwrap();
            closeReject();
        } catch {
            setActionError('Не удалось отклонить заявку');
            closeReject();
        }
    };

    return (
        <div data-testid="cafe-application-detail-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/cafe-applications')}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 transition hover:text-gray-900"
                    data-testid="cafe-application-back"
                >
                    <ArrowLeft size={16} />
                    К списку
                </button>
                <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[application.moderationStatus]}`}
                    data-testid="cafe-application-status"
                >
                    {STATUS_LABEL[application.moderationStatus]}
                </span>
            </div>

            {application.rejectionsCount > 0 && (
                <div
                    className="flex items-center gap-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-800"
                    data-testid="cafe-application-rejections-badge"
                >
                    <AlertTriangle size={16} />
                    <span>
                        Этот ИНН отклоняли {application.rejectionsCount}{' '}
                        {application.rejectionsCount === 1 ? 'раз' : 'раз(а)'}
                    </span>
                </div>
            )}

            <div className="rounded border border-gray-200 bg-white p-5 space-y-5">
                <section data-testid="cafe-application-info">
                    <h2 className="mb-3 text-base font-semibold text-gray-900">Кафе</h2>
                    <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                        <Field label="Название" value={application.name} />
                        <Field label="Slug" value={application.slug} />
                        <Field
                            label="Описание"
                            value={application.description}
                            wide
                        />
                        <Field label="Владелец (userId)" value={application.ownerUserId} />
                        <Field label="Создана" value={formatDateTime(application.createdAt)} />
                    </dl>
                </section>

                <CompanySection
                    company={application.company}
                    companyContactPhone={application.companyContactPhone}
                    companyVerifiedAt={application.companyVerifiedAt}
                />

                {(application.moderationStatus === 'approved' ||
                    application.moderationStatus === 'rejected') && (
                    <section
                        className="rounded border border-gray-200 bg-gray-50 p-4"
                        data-testid="cafe-application-decision"
                    >
                        <h2 className="mb-3 text-base font-semibold text-gray-900">Решение</h2>
                        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                            <Field
                                label="Дата решения"
                                value={formatDateTime(application.reviewedAt)}
                            />
                            <Field
                                label="Админ (id)"
                                value={application.reviewedByAdminId}
                            />
                            {application.moderationStatus === 'rejected' && (
                                <Field
                                    label="Причина отклонения"
                                    value={application.rejectionReason}
                                    wide
                                    testId="cafe-application-rejection-reason"
                                />
                            )}
                        </dl>
                    </section>
                )}
            </div>

            {actionError !== null && actionError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="cafe-application-action-error"
                >
                    <span>{actionError}</span>
                    <button
                        type="button"
                        onClick={() => setActionError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {application.moderationStatus === 'pending' && (
                <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                    <button
                        type="button"
                        onClick={openReject}
                        disabled={isApproving || isRejecting}
                        data-testid="cafe-application-reject-button"
                        className="inline-flex items-center gap-2 rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                    >
                        <X size={14} />
                        Отклонить
                    </button>
                    <button
                        type="button"
                        onClick={openApprove}
                        disabled={isApproving || isRejecting}
                        data-testid="cafe-application-approve-button"
                        className="inline-flex items-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
                    >
                        <Check size={14} />
                        Одобрить
                    </button>
                </div>
            )}

            <Modal
                isOpen={isApproveOpen}
                onClose={closeApprove}
                title="Одобрить заявку"
                testId="cafe-application-approve-modal"
            >
                <div className="space-y-4" data-testid="cafe-application-approve-form">
                    <p className="text-sm text-gray-600">
                        Кафе станет публичным и появится на витрине. Действие можно сопроводить
                        комментарием для аудита.
                    </p>
                    <Textarea
                        label="Комментарий (необязательно)"
                        value={approveReason}
                        onChange={(e) => setApproveReason(e.target.value)}
                        rows={3}
                        data-testid="cafe-application-approve-reason"
                    />
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={closeApprove}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void submitApprove();
                            }}
                            disabled={isApproving}
                            data-testid="cafe-application-approve-submit"
                            className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
                        >
                            Одобрить
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isRejectOpen}
                onClose={closeReject}
                title="Отклонить заявку"
                testId="cafe-application-reject-modal"
            >
                <div className="space-y-4" data-testid="cafe-application-reject-form">
                    <Textarea
                        label="Причина отклонения (обязательно)"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={5}
                        error={rejectReasonError ?? undefined}
                        data-testid="cafe-application-reject-reason"
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={closeReject}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void submitReject();
                            }}
                            disabled={isRejecting}
                            data-testid="cafe-application-reject-submit"
                            className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
                        >
                            Отклонить
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
